import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError, humanizeClientError } from "@/lib/api";
import { normalizeChatMessage, type ChatAccount, type ChatMessage } from "@/lib/chatConnect";

/**
 * Slack customer mode (Tip 148): accounts connected through the ATW Hub Slack app. Hub loads their
 * conversations live when opened, never in the background, and stores no message text.
 */

/** Fired after an install is claimed, so open pages reload their chat accounts. */
export const SLACK_CUSTOMER_CHANGED_EVENT = "atw-hub-slack-customer-changed";
export const SLACK_CUSTOMER_SETTINGS_PATH = "/settings/connections/chat";
export const NEEDS_YOU_PILOT_MESSAGE =
  "Needs you fills in once ATW Hub is approved in the Slack Marketplace.";

const HTTPS = /^https:\/\//i;

export function isSlackCustomerAccount(account: ChatAccount | null | undefined): boolean {
  return Boolean(account && account.providerId === "slack" && account.mode === "customer");
}

export function isListedSlackCustomer(account: ChatAccount | null | undefined): boolean {
  return isSlackCustomerAccount(account) && account?.listed === true;
}

function httpsOrUndefined(value: unknown): string | undefined {
  return typeof value === "string" && HTTPS.test(value) ? value : undefined;
}

export type SlackCustomerStatus = {
  /** The ATW Hub Slack app's credentials are on the API host. */
  configured: boolean;
  /** Slack approved the app in the Marketplace; false means pilot. */
  listed: boolean;
  installUrl: string;
  accounts: ChatAccount[];
};

/** `GET /api/chat/slack-customer/status`; null on Hub API builds before Tip 148. */
export async function fetchSlackCustomerStatus(): Promise<SlackCustomerStatus | null> {
  try {
    const raw = await api<Record<string, unknown>>("/api/chat/slack-customer/status");
    const accounts = Array.isArray(raw.accounts) ? (raw.accounts as ChatAccount[]) : [];
    return {
      configured: raw.configured === true,
      listed: raw.listed === true,
      installUrl: httpsOrUndefined(raw.installUrl) || "",
      accounts: accounts.filter(isSlackCustomerAccount),
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

function slackErrorDetails(err: unknown): Record<string, unknown> {
  return err instanceof ApiError && err.details && typeof err.details === "object"
    ? (err.details as Record<string, unknown>)
    : {};
}

/** Seconds to wait when the Hub API passed on a Slack rate limit (HTTP 429), else null. */
export function slackRetryAfter(err: unknown): number | null {
  if (!(err instanceof ApiError) || err.status !== 429) return null;
  const seconds = Number(slackErrorDetails(err).retryAfter);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 30;
}

/** Slack link the API attaches to customer-mode errors, so people can open it in Slack instead. */
export function slackErrorPermalink(err: unknown): string | undefined {
  return httpsOrUndefined(slackErrorDetails(err).permalink);
}

export type SlackCustomerPage = {
  /** Oldest first. */
  messages: ChatMessage[];
  hasMore: boolean;
  /** Listed only: pass back to load the next older page. */
  nextCursor?: string;
  /** Pilot: Slack gives apps outside the Marketplace only the latest messages. */
  limited: boolean;
  /** The conversation in Slack. */
  permalink?: string;
};

/** Newest page of a conversation, or an older one with `cursor` — `GET …/messages?envelope=1`. */
export async function fetchSlackCustomerMessages(
  accountId: string,
  channelId: string,
  cursor?: string,
): Promise<SlackCustomerPage> {
  const qs = new URLSearchParams({ accountId, envelope: "1" });
  if (cursor) qs.set("cursor", cursor);
  const raw = await api<unknown>(`/api/chat/channels/${encodeURIComponent(channelId)}/messages?${qs}`);
  const envelope =
    raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const list = Array.isArray(raw) ? raw : Array.isArray(envelope.messages) ? envelope.messages : [];
  return {
    messages: list
      .map((row, index) => normalizeChatMessage(row, index))
      .filter((m): m is ChatMessage => Boolean(m))
      .sort((a, b) => (a.at ?? 0) - (b.at ?? 0)),
    hasMore: envelope.hasMore === true,
    nextCursor:
      typeof envelope.nextCursor === "string" && envelope.nextCursor ? envelope.nextCursor : undefined,
    limited: envelope.limited === true,
    permalink: httpsOrUndefined(envelope.permalink),
  };
}

export type NeedsYouItem = {
  kind: "mention" | "dm";
  channelId: string;
  channelName?: string;
  ts: string;
  /** Set when the message is a reply in a thread (Hub Chat shows conversations, not threads). */
  threadTs?: string;
  author: string;
  text: string;
  permalink?: string;
  /** Epoch ms. */
  at: number | null;
};

export type NeedsYouSnapshot = {
  accountId: string;
  items: NeedsYouItem[];
  /** One of the two searches failed; the items come from the other. */
  partialError?: string;
};

const NEEDS_YOU_REUSE_MS = 60 * 1000;
const needsYouCache = new Map<string, { at: number; snapshot: NeedsYouSnapshot }>();

/**
 * Mentions and DMs waiting on a reply from the last 7 days — `GET /api/chat/needs-you`, Slack
 * Real-time Search as the person. Listed accounts only. This tab reuses a result for a minute so
 * Hub Home and Chat Home share one search; `force` skips that.
 */
export async function fetchNeedsYou(
  accountId: string,
  opts?: { force?: boolean },
): Promise<NeedsYouSnapshot> {
  const hit = needsYouCache.get(accountId);
  if (!opts?.force && hit && Date.now() - hit.at < NEEDS_YOU_REUSE_MS) return hit.snapshot;
  const raw = await api<Record<string, unknown>>(
    `/api/chat/needs-you?accountId=${encodeURIComponent(accountId)}`,
  );
  const items: NeedsYouItem[] = [];
  for (const entry of Array.isArray(raw.items) ? raw.items : []) {
    if (!entry || typeof entry !== "object") continue;
    const row = entry as Record<string, unknown>;
    const channelId = typeof row.channelId === "string" ? row.channelId : "";
    const ts = typeof row.ts === "string" ? row.ts : "";
    if (!channelId || !ts) continue;
    items.push({
      kind: row.kind === "dm" ? "dm" : "mention",
      channelId,
      channelName: typeof row.channelName === "string" && row.channelName ? row.channelName : undefined,
      ts,
      threadTs: typeof row.threadTs === "string" && row.threadTs ? row.threadTs : undefined,
      author: typeof row.author === "string" && row.author ? row.author : "Someone",
      text: typeof row.text === "string" ? row.text : "",
      permalink: httpsOrUndefined(row.permalink),
      at: typeof row.at === "number" && Number.isFinite(row.at) ? row.at : null,
    });
  }
  const snapshot: NeedsYouSnapshot = {
    accountId,
    items,
    partialError: raw.partial === true && typeof raw.error === "string" ? raw.error : undefined,
  };
  needsYouCache.set(accountId, { at: Date.now(), snapshot });
  return snapshot;
}

function needsYouError(err: unknown): string {
  const wait = slackRetryAfter(err);
  if (wait != null) {
    return `Slack is limiting how often ATW Hub can check what needs you. Try again in ${wait} seconds.`;
  }
  return humanizeClientError(err, "Could not check what needs you in Slack.");
}

export type NeedsYouState = {
  loading: boolean;
  snapshots: Record<string, NeedsYouSnapshot>;
  errors: Record<string, string>;
};

/** Runs Needs you for the listed customer accounts while `active` (Home is open). */
export function useNeedsYou(accounts: ChatAccount[], active: boolean, refreshNonce = 0): NeedsYouState {
  const listedKey = accounts
    .filter(isListedSlackCustomer)
    .map((a) => a.id)
    .join(",");
  const [state, setState] = useState<NeedsYouState>({ loading: false, snapshots: {}, errors: {} });
  const lastNonce = useRef(refreshNonce);

  useEffect(() => {
    const ids = listedKey ? listedKey.split(",") : [];
    if (!active || !ids.length) return;
    const force = lastNonce.current !== refreshNonce;
    lastNonce.current = refreshNonce;
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));
    void Promise.allSettled(ids.map((id) => fetchNeedsYou(id, { force }))).then((results) => {
      if (cancelled) return;
      const snapshots: Record<string, NeedsYouSnapshot> = {};
      const errors: Record<string, string> = {};
      results.forEach((result, i) => {
        const id = ids[i]!;
        if (result.status === "fulfilled") snapshots[id] = result.value;
        else errors[id] = needsYouError(result.reason);
      });
      setState({ loading: false, snapshots, errors });
    });
    return () => {
      cancelled = true;
    };
  }, [listedKey, active, refreshNonce]);

  return state;
}

export type SavedFromSlack = {
  id: string;
  channelId: string;
  ts: string;
  permalink: string;
  savedAt: string;
  accountId?: string;
  teamName?: string;
  /** False when the Slack account that saved it isn't connected to Hub anymore. */
  connected: boolean;
};

/** Links saved with the Send to Hub message shortcut, newest first — `GET /api/chat/saved`. */
export async function fetchSavedFromSlack(): Promise<SavedFromSlack[]> {
  let raw: { items?: unknown };
  try {
    raw = await api<{ items?: unknown }>("/api/chat/saved");
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return [];
    throw err;
  }
  const out: SavedFromSlack[] = [];
  for (const entry of Array.isArray(raw.items) ? raw.items : []) {
    if (!entry || typeof entry !== "object") continue;
    const row = entry as Record<string, unknown>;
    const id = typeof row.id === "string" ? row.id : "";
    const permalink = httpsOrUndefined(row.permalink);
    if (!id || !permalink) continue;
    out.push({
      id,
      channelId: String(row.channelId || ""),
      ts: String(row.ts || ""),
      permalink,
      savedAt: String(row.savedAt || ""),
      accountId: typeof row.accountId === "string" ? row.accountId : undefined,
      teamName: typeof row.teamName === "string" ? row.teamName : undefined,
      connected: row.connected === true,
    });
  }
  return out;
}

/** The saved message, loaded live from Slack — `GET /api/chat/saved/:id/message`. */
export async function fetchSavedMessage(id: string): Promise<ChatMessage | null> {
  const raw = await api<{ found?: boolean; message?: unknown }>(
    `/api/chat/saved/${encodeURIComponent(id)}/message`,
  );
  return raw.found === true ? normalizeChatMessage(raw.message, 0) : null;
}

export async function removeSavedFromSlack(id: string): Promise<void> {
  await api<unknown>(`/api/chat/saved/${encodeURIComponent(id)}`, { method: "DELETE" });
}

const CLAIM_KEY = "atw-hub-slack-claim";
const CLAIM_RESULT_KEY = "atw-hub-slack-claim-result";
/** The API keeps an unclaimed install for 30 minutes. */
const CLAIM_TTL_MS = 30 * 60 * 1000;
const CLAIM_HASH = /(?:^#|&)slack-claim=([A-Za-z0-9_-]{20,200})(?:&|$)/;

type PendingClaim = { code: string; at: number };

export type SlackClaimResult = { ok: boolean; message: string };

/**
 * Slack's install callback lands on `/settings/connections/chat#slack-claim=CODE`. Call before the
 * router renders: signing in first (auth.atw.net and back) would drop the hash. The code stays in
 * this tab's sessionStorage until someone signed in to Hub claims it.
 */
export function captureSlackClaim(): void {
  const match = CLAIM_HASH.exec(window.location.hash);
  if (!match) return;
  try {
    sessionStorage.setItem(CLAIM_KEY, JSON.stringify({ code: match[1], at: Date.now() }));
  } catch {
    /* storage blocked: the install expires on the API and Connect starts over */
  }
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${window.location.search}`,
  );
}

function readPendingClaim(): PendingClaim | null {
  try {
    const raw = sessionStorage.getItem(CLAIM_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingClaim>;
    if (
      typeof parsed.code === "string" &&
      typeof parsed.at === "number" &&
      Date.now() - parsed.at < CLAIM_TTL_MS
    ) {
      return { code: parsed.code, at: parsed.at };
    }
    sessionStorage.removeItem(CLAIM_KEY);
  } catch {
    /* ignore */
  }
  return null;
}

export function hasPendingSlackClaim(): boolean {
  return readPendingClaim() != null;
}

export function peekSlackClaimResult(): SlackClaimResult | null {
  try {
    const raw = sessionStorage.getItem(CLAIM_RESULT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SlackClaimResult>;
    return typeof parsed.message === "string" ? { ok: parsed.ok === true, message: parsed.message } : null;
  } catch {
    return null;
  }
}

export function clearSlackClaimResult(): void {
  try {
    sessionStorage.removeItem(CLAIM_RESULT_KEY);
  } catch {
    /* ignore */
  }
}

async function claimSlackCustomerInstall(code: string): Promise<string> {
  const raw = await api<{ message?: string }>("/api/chat/slack-customer/claim", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
  return raw.message || "Slack is connected to ATW Hub.";
}

let claimInFlight = false;

/** Binds a captured install to whoever is signed in to Hub, then opens Connections → Chat. */
export function useSlackCustomerClaim(signedIn: boolean): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!signedIn || claimInFlight) return;
    const pending = readPendingClaim();
    if (!pending) return;
    claimInFlight = true;
    try {
      sessionStorage.removeItem(CLAIM_KEY);
    } catch {
      /* ignore */
    }
    void claimSlackCustomerInstall(pending.code)
      .then((message): SlackClaimResult | null => ({ ok: true, message }))
      .catch((err): SlackClaimResult | null => {
        if (err instanceof ApiError && err.status === 401) {
          // The Hub session had expired: keep the code for after sign-in.
          try {
            sessionStorage.setItem(CLAIM_KEY, JSON.stringify(pending));
          } catch {
            /* ignore */
          }
          return null;
        }
        return {
          ok: false,
          message: humanizeClientError(err, "Could not finish connecting Slack. Connect it again below."),
        };
      })
      .then((result) => {
        claimInFlight = false;
        if (!result) return;
        needsYouCache.clear();
        try {
          sessionStorage.setItem(CLAIM_RESULT_KEY, JSON.stringify(result));
        } catch {
          /* ignore */
        }
        window.dispatchEvent(new Event(SLACK_CUSTOMER_CHANGED_EVENT));
        if (window.location.pathname !== SLACK_CUSTOMER_SETTINGS_PATH) navigate(SLACK_CUSTOMER_SETTINGS_PATH);
      });
  }, [signedIn, navigate]);
}
